import Axios from "../../lib/Axios";

/**
 * Badge API Helper
 * 
 * API functions for badge management
 */

/**
 * Get badge list
 * @param {Object} filters - Filter parameters
 * @param {number} filters.limit - Pagination limit
 * @param {number} filters.page - Pagination page number
 * @returns {Promise} API response with badges array and pagination
 */
export const getBadgeList = async (filters = {}) => {
  const {
    limit = 50,
    page = 1,
  } = filters;

  const requestBody = {
    page: page,
    limit: limit,
  };

  // POST /cms/cms-get/badge-list
  const result = await Axios.post("cms/cms-get/badge-list", requestBody);
  const badges = result?.data?.result?.badges || result?.data?.result?.results || [];
  const pagination = result?.data?.result?.pagination || {};
  
  return {
    data: badges,
    pagination: {
      currentPage: pagination.currentPage || page,
      limit: pagination.limit || limit,
      totalPages: pagination.totalPages || 1,
      totalRecords: pagination.totalRecords || 0,
    },
  };
};

/**
 * Get badge details by ID
 * @param {number} badgeId - The badge ID
 * @returns {Promise} API response with badge details
 */
export const getBadgeDetails = async (badgeId) => {
  if (!badgeId) {
    throw new Error("badgeId is required");
  }
  
  // GET /admin/badges/{badgeId}
  const result = await Axios.get(`admin/badges/${badgeId}`);
  return result?.data?.result || result?.data;
};

/**
 * Delete a badge
 * @param {number} badgeId - The badge ID to delete
 * @returns {Promise} API response
 */
export const deleteBadge = async (badgeId) => {
  if (!badgeId) {
    throw new Error("badgeId is required");
  }
  
  // POST /cms/cms-delete/badge
  const result = await Axios.post("cms/cms-delete/badge", { badge_id: badgeId });
  return result;
};

/**
 * Create a new badge
 * @param {Object} badgeData - Badge data
 * @param {string} badgeData.badge_key - The badge key
 * @param {string} badgeData.badge_name - The badge name
 * @param {string} badgeData.description - The badge description
 * @param {string} badgeData.badge_type - The badge type
 * @returns {Promise} API response with created badge
 */
export const createBadge = async (badgeData) => {
  const { badge_key, badge_name, description, badge_type } = badgeData;
  
  if (!badge_key || !badge_name || !description || !badge_type) {
    throw new Error("All fields are required");
  }
  
  // POST /cms/cms-set/badge
  const result = await Axios.post("cms/cms-set/badge", {
    badge_key,
    badge_name,
    description,
    badge_type,
  });
  
  return result?.data?.result || result?.data;
};

/**
 * Edit/Update a badge
 * @param {Object} badgeData - Badge data
 * @param {number} badgeData.badge_id - The badge ID
 * @param {string} badgeData.badge_key - The badge key
 * @param {string} badgeData.badge_name - The badge name
 * @param {string} badgeData.description - The badge description
 * @param {string} badgeData.badge_type - The badge type
 * @returns {Promise} API response with updated badge
 */
export const editBadge = async (badgeData) => {
  const { badge_id, badge_key, badge_name, description, badge_type } = badgeData;
  
  if (!badge_id || !badge_key || !badge_name || !description || !badge_type) {
    throw new Error("All fields are required");
  }
  
  // POST /cms/cms-edit/badge
  const result = await Axios.post("cms/cms-edit/badge", {
    badge_id,
    badge_key,
    badge_name,
    description,
    badge_type,
  });
  
  return result?.data?.result || result?.data;
};

/**
 * Toggle badge active status
 * @param {number} badgeId - The badge ID
 * @param {boolean} isActive - The new active status
 * @returns {Promise} API response
 */
export const toggleBadgeStatus = async (badgeId, isActive) => {
  if (!badgeId) {
    throw new Error("badgeId is required");
  }
  
  // POST /admin/badges/toggle-status
  const result = await Axios.post("admin/badges/toggle-status", { 
    badgeId, 
    isActive 
  });
  return result;
};

